import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ClipboardCheck, Brain, AlertTriangle, Download, X, FileText } from 'lucide-react'
import api from '../../services/api'

function renderMarkdown(text: string) {
  return text
    .replace(/^#{1,3} (.+)$/gm, '<strong style="font-size:14px;color:var(--text)">$1</strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^---+$/gm, '<hr style="border:none;border-top:1px solid var(--border);margin:8px 0"/>')
    .replace(/\n/g, '<br/>')
}

const BAND_COLOUR: Record<string, string> = {
  pass: 'var(--green)', marginal: 'var(--amber)', fail: 'var(--red)',
}
const SEV_BADGE: Record<string, string> = {
  critical: 'badge-critical', major: 'badge-high', minor: 'badge-medium', observation: 'badge-low',
}

function ReportModal({ auditId, auditNumber, onClose }: { auditId: string; auditNumber: string; onClose: () => void }) {
  const [note, setNote] = useState('')
  const [sections, setSections] = useState({ summary: true, findings: true, signoff: true })
  const [downloading, setDownloading] = useState(false)
  const [dlError, setDlError] = useState('')

  const generate = async () => {
    setDlError('')
    setDownloading(true)
    try {
      const secs = ['cover', ...Object.entries(sections).filter(([, v]) => v).map(([k]) => k)].join(',')
      const params = new URLSearchParams({ sections: secs })
      if (note.trim()) params.set('note', note.trim())
      const res = await api.get(`/audits/${auditId}/report?${params}`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url
      a.download = `Audit-${auditNumber}.pdf`
      a.click()
      URL.revokeObjectURL(url)
      onClose()
    } catch {
      setDlError('Failed to generate report. Please try again.')
    } finally {
      setDownloading(false)
    }
  }

  const OPTS = [
    { key: 'summary' as const, label: 'Executive Summary', desc: 'Score bar, finding counts, AI summary' },
    { key: 'findings' as const, label: 'Detailed Findings', desc: 'All findings grouped by severity' },
    { key: 'signoff' as const, label: 'Sign-off Page', desc: 'Signature blocks and next steps' },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--white)',
        borderRadius: 16,
        boxShadow: 'var(--shadow-lg)',
        width: '100%',
        maxWidth: 460,
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={18} style={{ color: 'var(--accent)', flexShrink: 0 }} />
            <span style={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>Generate PDF Report</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--text-muted)' }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)' }}>{auditNumber}</div>

        {/* Sections */}
        <div>
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>Sections to include</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {OPTS.map(({ key, label, desc }) => (
              <div key={key} onClick={() => setSections(s => ({ ...s, [key]: !s[key] }))}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                  background: sections[key] ? 'var(--accent-light)' : 'var(--bg)',
                  border: `1.5px solid ${sections[key] ? 'var(--accent)' : 'var(--border)'}`,
                  transition: 'all 0.15s',
                }}>
                <input
                  type="checkbox"
                  checked={sections[key]}
                  onChange={() => {}}
                  style={{ flexShrink: 0, width: 16, height: 16, accentColor: 'var(--accent)', cursor: 'pointer' }}
                />
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: 0 }}>{label}</p>
                  <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0, marginTop: 2 }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Executive note */}
        <div>
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
            Executive note <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span>
          </p>
          <textarea
            rows={3}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Add a custom note to appear in the Executive Summary section…"
            style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
          />
        </div>

        {dlError && (
          <p style={{ fontSize: 13, color: 'var(--red)', background: 'var(--red-light)', padding: '8px 12px', borderRadius: 8 }}>{dlError}</p>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose} disabled={downloading}>Cancel</button>
          <button className="btn btn-primary" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }} onClick={generate} disabled={downloading}>
            <Download size={15} /> {downloading ? 'Generating…' : 'Download PDF'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function AuditDetail() {
  const { id } = useParams<{ id: string }>()
  const [audit, setAudit] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [showReport, setShowReport] = useState(false)

  useEffect(() => {
    api.get(`/audits/${id}`).then(r => setAudit(r.data)).finally(() => setLoading(false))
  }, [id])

  if (loading) return <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Loading...</div>
  if (!audit) return <div className="text-center py-16" style={{ color: 'var(--red)' }}>Audit not found</div>

  const isDone = audit.status === 'completed'
  const findings = audit.findings || []
  const critical = findings.filter((f: any) => f.severity === 'critical')
  const major = findings.filter((f: any) => f.severity === 'major')

  return (
    <>
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/audits" className="text-sm" style={{ color: 'var(--accent)' }}>← Audits</Link>
          <h1 className="text-2xl font-bold mt-1" style={{ color: 'var(--text)' }}>{audit.audit_number}</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            {audit.audit_type?.replace('_', ' ').toUpperCase()} · {audit.site_name || 'All sites'} · {audit.auditor_name}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {audit.overall_score !== null && (
            <div className="text-right">
              <p className="text-3xl font-bold" style={{ color: BAND_COLOUR[audit.score_band] || 'var(--text)' }}>{audit.overall_score}%</p>
              <p className="text-xs capitalize" style={{ color: BAND_COLOUR[audit.score_band] || 'var(--text-muted)' }}>{audit.score_band}</p>
            </div>
          )}
          {isDone && (
            <button
              onClick={() => setShowReport(true)}
              className="btn btn-secondary flex items-center gap-1.5"
            >
              <Download size={15} /> PDF Report
            </button>
          )}
          {!isDone && (
            <Link to={`/audits/${id}/conduct`} className="btn btn-primary">Continue Audit</Link>
          )}
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Findings', value: findings.length, colour: findings.length > 0 ? 'var(--amber)' : 'var(--green)' },
          { label: 'Critical', value: audit.critical_findings || 0, colour: (audit.critical_findings || 0) > 0 ? 'var(--red)' : 'var(--green)' },
          { label: 'Major', value: audit.major_findings || 0, colour: (audit.major_findings || 0) > 0 ? 'var(--orange)' : 'var(--green)' },
          { label: 'Minor / Obs', value: (audit.minor_findings || 0) + (audit.observations || 0), colour: 'var(--text)' },
        ].map(({ label, value, colour }) => (
          <div key={label} className="card text-center">
            <p className="text-3xl font-bold" style={{ color: colour }}>{value}</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{label}</p>
          </div>
        ))}
      </div>

      {/* Critical findings alert */}
      {critical.length > 0 && (
        <div className="card p-4" style={{ background: 'var(--red-light)', border: '1px solid var(--red)' }}>
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={18} style={{ color: 'var(--red)', flexShrink: 0 }} />
            <p className="font-bold text-sm" style={{ color: 'var(--red)' }}>Critical Findings — Immediate Action Required</p>
          </div>
          <div className="space-y-2">
            {critical.map((f: any) => (
              <div key={f.id} className="text-sm" style={{ color: 'var(--red)' }}>
                <p className="font-medium">{f.description}</p>
                {f.recommended_action && <p className="text-xs mt-0.5 opacity-80">→ {f.recommended_action}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Executive Summary */}
      {audit.ai_executive_summary && (
        <div className="card space-y-3">
          <div className="flex items-center gap-2">
            <Brain size={18} style={{ color: 'var(--purple)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>AI Executive Summary</h2>
          </div>
          <div className="text-sm" style={{ color: 'var(--text)', lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: renderMarkdown(audit.ai_executive_summary) }} />
          {audit.ai_improvement_areas && (
            <div className="p-3 rounded-xl" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>IMPROVEMENT PRIORITIES</p>
              <div className="text-sm" style={{ color: 'var(--text)', lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: renderMarkdown(audit.ai_improvement_areas) }} />
            </div>
          )}
          <p className="ai-label text-xs">AI-assisted summary — requires human review (NSW Digital Work Systems Act 2026)</p>
        </div>
      )}

      {/* All findings */}
      {findings.length > 0 && (
        <div className="card space-y-3">
          <h2 className="font-semibold" style={{ color: 'var(--text)' }}>All Findings ({findings.length})</h2>
          <div className="space-y-2">
            {findings.map((f: any) => (
              <div key={f.id} className="p-3 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                <div className="flex items-start gap-3">
                  <span className={`badge ${SEV_BADGE[f.severity] || ''} flex-shrink-0`}>{f.severity}</span>
                  <div className="flex-1 min-w-0">
                    {f.section_name && <p className="text-xs font-semibold mb-0.5" style={{ color: 'var(--text-muted)' }}>{f.section_name}</p>}
                    <p className="text-sm" style={{ color: 'var(--text)' }}>{f.description}</p>
                    {f.regulation_reference && <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>Ref: {f.regulation_reference}</p>}
                    {f.recommended_action && <p className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>→ {f.recommended_action}</p>}
                  </div>
                  <span className={`badge capitalize flex-shrink-0 ${f.status === 'open' ? 'badge-high' : 'badge-low'}`}>{f.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audit info */}
      <div className="card">
        <h2 className="font-semibold mb-4" style={{ color: 'var(--text)' }}>Audit Details</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          {[
            ['Template', audit.template_name || 'None'],
            ['Scheduled', audit.scheduled_date ? new Date(audit.scheduled_date).toLocaleDateString('en-AU') : '—'],
            ['Completed', audit.end_date ? new Date(audit.end_date).toLocaleDateString('en-AU') : '—'],
            ['Pass Threshold', audit.pass_threshold ? `${audit.pass_threshold}%` : '—'],
            ['Score Band', audit.score_band?.toUpperCase() || '—'],
          ].map(([label, val]) => (
            <div key={label}>
              <p className="text-xs font-semibold mb-0.5" style={{ color: 'var(--text-muted)' }}>{label}</p>
              <p style={{ color: 'var(--text)' }}>{val}</p>
            </div>
          ))}
        </div>
      </div>
    </div>

    {showReport && (
      <ReportModal
        auditId={id!}
        auditNumber={audit.audit_number}
        onClose={() => setShowReport(false)}
      />
    )}
    </>
  )
}
